import moment from 'moment/moment'

export const LEAVE_COLORS = Object.freeze({
  paid_leave: '#77B017',
  unpaid_leave: '#F2C94C',
  sick_leave: '#EB5757',
  maternity_leave: '#2D9CDB',
  paternity_leave: '#2D9CDB',
})
export const STATUS_OPTIONS = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'requested', label: 'Requested' },
  { value: 'approved', label: 'Approved' },
])
export const LEGEND_ITEMS = Object.freeze([
  { code: 'paid_leave', name: 'Paid leave', color: LEAVE_COLORS.paid_leave },
  { code: 'unpaid_leave', name: 'Unpaid leave', color: LEAVE_COLORS.unpaid_leave },
  { code: 'sick_leave', name: 'Sick leave', color: LEAVE_COLORS.sick_leave },
  { code: 'parental_leave', name: 'Maternity / Paternity leave', color: LEAVE_COLORS.maternity_leave },
])

const defaultColor = LEAVE_COLORS.paid_leave
const periodWidth = 42
const periodHeight = 58

export function createDefaultDates() {
  return {
    start: moment().format('YYYY-MM-DD'),
    end: moment().add(30, 'days').format('YYYY-MM-DD'),
  }
}

export function colorToRgba(color, alpha) {
  const normalizedColor = /^#[0-9a-f]{6}$/i.test(color || '') ? color : defaultColor
  const number = parseInt(normalizedColor.slice(1), 16)
  return `rgba(${(number >> 16) & 255}, ${(number >> 8) & 255}, ${number & 255}, ${alpha})`
}

export function getLeaveColor(event) {
  const leaveTypeCode = event && event.leave_type && event.leave_type.code
  const color = LEAVE_COLORS[leaveTypeCode] || (event && event.color)
  return /^#[0-9a-f]{6}$/i.test(color || '') ? color : defaultColor
}

export function getProofPresentation(event) {
  if (!event) return { state: 'not-required', name: 'Not required', description: '' }
  if (event.proof) return { state: 'attached', name: 'Attached', description: '' }

  const requirement = event.proof_requirement
  if (requirement && requirement.code === 'optional') {
    return { state: 'optional', name: 'Optional', description: '' }
  }
  if (requirement && requirement.required) {
    const description = requirement.can_upload_later ? 'Required later' : requirement.name
    return { state: 'missing', name: 'Missing', description: description === 'Missing' ? '' : description }
  }
  return { state: 'not-required', name: 'Not required', description: '' }
}

function buildPeriod(start, end) {
  const period = []
  const cursor = start.clone()
  while (cursor.isSameOrBefore(end, 'day')) {
    period.push({
      date: cursor.format('YYYY-MM-DD'),
      label: cursor.format('D'),
      month: cursor.format('YYYY-MM'),
      monthLabel: cursor.format('MMMM YYYY'),
      weekday: cursor.format('ddd'),
    })
    cursor.add(1, 'day')
  }
  return period
}

function buildEmployeeRows(events) {
  const instances = []
  const rows = {}
  events.forEach(event => {
    if (rows[event.employee.id] !== undefined) return
    rows[event.employee.id] = instances.length
    instances.push({
      id: event.employee.id,
      name: event.employee.full_name,
      description: event.department && event.department.name,
    })
  })
  return { instances, rows }
}

function createEventStyle(color, isRequested) {
  const style = {
    backgroundColor: colorToRgba(color, 0.35),
    border: '1px solid transparent',
    borderLeft: `5px solid ${color}`,
    color: '#263238',
  }

  if (isRequested) {
    return {
      ...style,
      border: `2px dashed ${color}`,
      borderLeft: `5px solid ${color}`,
      boxShadow: 'none',
    }
  }
  return style
}

function buildEvent(event, start, end, rows, dayWidth) {
  const color = getLeaveColor(event)
  const visibleStart = moment.max(moment(event.from_date), start)
  const visibleEnd = moment.min(moment(event.to_date), end)
  const isRequested = event.status === 'requested'
  return {
    ...event,
    color,
    x: visibleStart.diff(start, 'days') * dayWidth,
    y: rows[event.employee.id] * periodHeight,
    width: (visibleEnd.diff(visibleStart, 'days') + 1) * dayWidth,
    title: event.leave_type.name,
    description: `${moment(event.from_date).format('DD MMM')}–${moment(event.to_date).format('DD MMM')}`,
    interactive: true,
    cssClass: isRequested ? 'schedule-calendar__event--requested' : '',
    style: createEventStyle(color, isRequested),
  }
}

export function buildLeaveSchedule(events, dates, dayWidth = periodWidth) {
  const normalizedEvents = Array.isArray(events) ? events : []
  const start = moment(dates.start)
  const end = moment(dates.end)
  const { instances, rows } = buildEmployeeRows(normalizedEvents)
  return {
    period: buildPeriod(start, end),
    period_width: dayWidth,
    period_height: periodHeight,
    timeline: null,
    instances,
    events: normalizedEvents.map(event => buildEvent(event, start, end, rows, dayWidth)),
  }
}
